export type NativeGeocoderResult = {
  latitude: number;
  longitude: number;
  address: string | null;
  countryCode: string | null;
  countryName: string | null;
  postalCode: string | null;
  administrativeArea: string | null;
  subAdministrativeArea: string | null;
  locality: string | null;
  subLocality: string | null;
  thoroughfare: string | null;
  subThoroughfare: string | null;
  areasOfInterest: string[] | null;
};

// Ellipsoid constants for WGS84
const EARTH_RADIUS = 6378137.0;
const ECC_SQ = 0.00669342162296594323;

// Core distortion function used in GCJ-02
function transformOffset(x: number, y: number) {
  const xy = x * y;
  const absX = Math.sqrt(Math.abs(x));
  const xPi = x * Math.PI;
  const yPi = y * Math.PI;

  let latitude =
    20 * Math.sin(6 * xPi) +
    20 * Math.sin(2 * xPi) +
    20 * Math.sin(yPi) +
    40 * Math.sin(yPi / 3) +
    160 * Math.sin(yPi / 12) +
    320 * Math.sin(yPi / 30);

  let longitude =
    20 * Math.sin(6 * xPi) +
    20 * Math.sin(2 * xPi) +
    20 * Math.sin(xPi) +
    40 * Math.sin(xPi / 3) +
    150 * Math.sin(xPi / 12) +
    300 * Math.sin(xPi / 30);

  latitude = (latitude * 2) / 3;
  longitude = (longitude * 2) / 3;

  latitude += -100 + 2 * x + 3 * y + 0.2 * y * y + 0.1 * xy + 0.2 * absX;
  longitude += 300 + x + 2 * y + 0.1 * x * x + 0.1 * xy + 0.1 * absX;

  return { latitude, longitude };
}

// Compute GCJ-02 deltas for a given WGS84 point
function computeDelta({ latitude, longitude }: { latitude: number, longitude: number }) {
  const delta = transformOffset(longitude - 105, latitude - 35);
  const radLat = (latitude * Math.PI) / 180;

  let magic = Math.sin(radLat);
  magic = 1 - ECC_SQ * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  delta.latitude =
    (delta.latitude * 180) /
    (((EARTH_RADIUS * (1 - ECC_SQ)) / (magic * sqrtMagic)) * Math.PI);

  delta.longitude =
    (delta.longitude * 180) /
    ((EARTH_RADIUS / sqrtMagic) * Math.cos(radLat) * Math.PI);

  return delta;
}

// WGS84 to GCJ-02
function wgs84ToGcj02({ latitude, longitude }: { latitude: number, longitude: number }) {
  const delta = computeDelta({ latitude, longitude });
  return { latitude: latitude + delta.latitude, longitude: longitude + delta.longitude };
}

// GCJ-02 to BD-09
function gcj02ToBd09({ latitude, longitude }: { latitude: number, longitude: number }) {
  const xpi = (Math.PI * 3000) / 180;
  const z = Math.sqrt(longitude * longitude + latitude * latitude) + 0.00002 * Math.sin(latitude * xpi);
  const theta = Math.atan2(latitude, longitude) + 0.000003 * Math.cos(longitude * xpi);

  const bdLongitude = z * Math.cos(theta) + 0.0065;
  const bdLatitude = z * Math.sin(theta) + 0.006;

  return { latitude: bdLatitude, longitude: bdLongitude };
}

// WGS84 to BD-09 (full chain)
function wgs84ToBd09(wgs84: { latitude: number, longitude: number }) {
  const gcj = wgs84ToGcj02(wgs84);
  return gcj02ToBd09(gcj);
}

export function toBaiduCoords(coords: { latitude: number, longitude: number }) {
  return wgs84ToBd09(coords);
}

declare var nativegeocoder: any;

export async function forwardGeocode(address: string): Promise<NativeGeocoderResult[]> {
  let results = [];
  try {
    if (typeof nativegeocoder === 'undefined') {
      throw new Error('NativeGeocoder is not loaded');
    }
    results = await new Promise((res, rej) => nativegeocoder.forwardGeocode(
      res,
      rej,
      address, {
      useLocale: true,
      maxResults: 5
    }));
  } catch (error) {
    console.error('forwardGeocode: nativeGeocoder failed, falling back to Google Maps Geocoder:', error);
    results = await geocodeWithGoogleMaps({ address });
  }
  if (results.length === 0) {
    results = [
      {
        address: address,
      } as NativeGeocoderResult
    ]
  }
  return results;
}

export async function reverseGeocode(lat: number, lng: number): Promise<NativeGeocoderResult[]> {
  let results = [];
  try {
    if (typeof nativegeocoder === 'undefined') {
      throw new Error('NativeGeocoder is not loaded');
    }
    results = await new Promise((res, rej) => nativegeocoder.reverseGeocode(
      res,
      rej,
      lat,
      lng, {
      useLocale: true,
      maxResults: 5
    }));
  } catch (error) {
    console.error('reverseGeocode: nativeGeocoder failed, falling back to Google Maps Geocoder:', error);
    results = await geocodeWithGoogleMaps({ location: { lat, lng } });
  }
  if (results.length === 0) {
    results = [
      {
        latitude: lat,
        longitude: lng,
      } as NativeGeocoderResult
    ]
  }
  return results.map(formatAddress);
}

function formatAddress(r) {
  if (r.address) {
    return r;
  }
  let address = r.addressLines?.join(", ")
  if (!address) {
    const line1 = [r.subThoroughfare, r.thoroughfare].filter(Boolean).join(" ");
    const line2 = [r.subLocality, r.locality].filter(Boolean).join(", ");
    const line3 = [r.administrativeArea, r.postalCode].filter(Boolean).join(" ");
    const line4 = r.countryName;

    address = [line1, line2, line3, line4].filter(Boolean).join(", ");
  }
  r.address = address;
  return r;
}

export async function checkIfChinaCoords(latitude: number, longitude: number) {
  try {
    const result = await reverseGeocode(latitude, longitude);
    if (result.length > 0 && result[0].countryCode) {
      return result[0].countryCode === 'CN';
    }
    return false;
  } catch (error) {
    console.log(error);
    return false;
  }
}

export async function convertAddressToCoords(address: string): Promise<{ latitude: number, longitude: number } | false> {
  try {
    const result = await forwardGeocode(address);
    if (!result.length) return false;
    let coords = {
      latitude: +result[0].latitude,
      longitude: +result[0].longitude
    };
    if (await checkIfChinaCoords(coords.latitude, coords.longitude)) {
      coords = toBaiduCoords(coords);
    }
    return coords;
  } catch (error) {
    console.log(error);
  }
}

async function geocodeWithGoogleMaps(request: { address?: string; location?: { lat: number; lng: number } }): Promise<NativeGeocoderResult[]> {
  if (typeof google === 'undefined' || !google.maps?.Geocoder) {
    throw new Error('Google Maps API is not loaded');
  }
  const res = await new google.maps.Geocoder().geocode(request);
  return res.results.map(result => convertGoogleGeocoderResult(result));
}

function convertGoogleGeocoderResult(result: google.maps.GeocoderResult): NativeGeocoderResult {
  const addressComponents = result.address_components || [];
  const location = result.geometry?.location;

  const getAddressComponent = (type: string) => {
    const component = addressComponents.find(comp => comp.types.includes(type));
    return component ? component.long_name : null;
  };

  const getAddressComponentShort = (type: string) => {
    const component = addressComponents.find(comp => comp.types.includes(type));
    return component ? component.short_name : null;
  };

  return {
    latitude: location ? +location.lat() : 0,
    longitude: location ? +location.lng() : 0,
    address: result.formatted_address || null,
    countryCode: getAddressComponentShort('country') || null,
    countryName: getAddressComponent('country') || null,
    postalCode: getAddressComponent('postal_code') || null,
    administrativeArea: getAddressComponent('administrative_area_level_1') || null,
    subAdministrativeArea: getAddressComponent('administrative_area_level_2') || null,
    locality: getAddressComponent('locality') || getAddressComponent('sublocality_level_1') || null,
    subLocality: getAddressComponent('sublocality') || getAddressComponent('sublocality_level_1') || null,
    thoroughfare: getAddressComponent('route') || null,
    subThoroughfare: getAddressComponent('street_number') || null,
    areasOfInterest: getAddressComponent('point_of_interest') ? [getAddressComponent('point_of_interest')] : null,
  };
}

export function calculateDistance(
  fromCoords: { latitude: number, longitude: number },
  toCoords: { latitude: number, longitude: number },
  unit: 'K' | 'N' | 'M' | 'MI' = 'MI'
): number {
  if ((fromCoords.latitude == toCoords.latitude) && (fromCoords.longitude == toCoords.longitude)) {
    return 0;
  }
  const toRad = (deg: number) => deg * Math.PI / 180;

  // Earth radius in kilometers
  const R_KM = 6371.0;

  // Calculate the differences in latitude and longitude
  const dLat = toRad(toCoords.latitude - fromCoords.latitude);
  const dLon = toRad(toCoords.longitude - fromCoords.longitude);

  // Convert latitude values to radians
  const rLat1 = toRad(fromCoords.latitude);
  const rLat2 = toRad(toCoords.latitude);

  // Calculate the angle using the Haversine formula
  // a = sin²(Δlat/2) + cos(lat1) * cos(lat2) * sin²(Δlon/2)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) ** 2;

  // angle = 2 * atan2(√a, √(1-a))
  const angle = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // distance = R * angle (where R is the Earth radius in kilometers)
  let distance = R_KM * angle;

  const unitConversions: Record<string, number> = {
    K: 1,              // Kilometers (already in km, return as-is)
    N: 0.539957,       // Nautical miles (km to nautical miles: divide by 1.852)
    M: 1000,            // Meters (km to meters: multiply by 1000)
    MI: 0.621371        // Miles (km to miles: multiply by 0.621371)
  };

  return distance * (unitConversions[unit] || unitConversions.MI);
}

type Key = string | number | symbol;
type KeyFn<T> = (item: T) => Key | null | undefined;
type ValueFn<T, V> = (item: T) => V;


export function groupBy<T>(
  data: Iterable<T>,
  specs: readonly (readonly KeyFn<T>[])[]
): any[];

export function groupBy<
  T,
  Specs extends readonly (readonly KeyFn<T>[])[],
  Values extends { [K in keyof Specs]: ValueFn<T, any> },
  LeafModes extends { [K in keyof Specs]: boolean }
>(
  data: Iterable<T>,
  specs: Specs,
  values: Values,
  leafIsArray?: LeafModes
): any[];

export function groupBy<T>(
  data: Iterable<T>,
  specs: readonly (readonly KeyFn<T>[])[],
  values?: readonly ValueFn<T, any>[],
  leafIsArray?: readonly boolean[]
): any[] {
  const roots = specs.map(() => ({} as Record<string, any>));
  const valueFns =
    values ?? (specs.map(() => ((x: T) => x)) as any);
  const leafModes = leafIsArray ?? (specs.map(() => false) as any);

  if (valueFns.length !== specs.length) {
    throw new Error("Value selectors count must match group specs count");
  }
  if (leafModes.length !== specs.length) {
    throw new Error("Leaf mode count must match group specs count");
  }

  for (const item of data) {
    for (let s = 0; s < specs.length; s++) {
      const fns = specs[s];
      const valueFn = valueFns[s];
      const isArray = leafModes[s];
      let node: any = roots[s];

      for (let d = 0; d < fns.length; d++) {
        const key = fns[d](item);
        if (key == null) {
          node = null;
          break;
        }

        const ks = String(key);

        if (d === fns.length - 1) {
          if (isArray) {
            const arr = node[ks] ?? (node[ks] = []);
            arr.push(valueFn(item));
          } else {
            node[ks] = valueFn(item);
          }
        } else {
          node = node[ks] ?? (node[ks] = {});
        }
      }
    }
  }

  return roots;
}
